import { AdminV1OrganizationsPaymentsStatisticsListMode } from "@/api/model/adminV1OrganizationsPaymentsStatisticsListMode";
import { AdminV1OrganizationsPaymentsLogListStatus } from "@/api/model/adminV1OrganizationsPaymentsLogListStatus";
import { AdminV1OrganizationsPaymentsLogListType } from "@/api/model/adminV1OrganizationsPaymentsLogListType";
import type { LineItem } from "@/types";

export const ACTIVE_INVOICE_DISPLAY = "Active";
export const PAID_INVOICE_DISPLAY = "Paid";
export const PAST_DUE_INVOICE_DISPLAY = "Past Due";
export const DRAFT_INVOICE_DISPLAY = "Draft";
export const PARTIALLY_PAID_INVOICE_DISPLAY = "Partially Paid";
export const CANCELLED_INVOICE_DISPLAY = "Canceled";
export const UNKNOWN_INVOICE_DISPLAY = "Unknown";

export const INVOICE_STATUSES_DISPLAY = [
  ACTIVE_INVOICE_DISPLAY,
  PAID_INVOICE_DISPLAY,
  PAST_DUE_INVOICE_DISPLAY,
  DRAFT_INVOICE_DISPLAY,
  PARTIALLY_PAID_INVOICE_DISPLAY,
  CANCELLED_INVOICE_DISPLAY,
  UNKNOWN_INVOICE_DISPLAY,
];

export const ACTIVE_INVOICE = "active";
export const PAID_INVOICE = "paid";
export const PAST_DUE_INVOICE = "past due";
export const DRAFT_INVOICE = "draft";
export const PARTIALLY_PAID_INVOICE = "partially paid";
export const CANCELLED_INVOICE = "cancelled";
export const UNKNOWN_INVOICE = "unknown";

export const INVOICE_STATUSES = [
  ACTIVE_INVOICE,
  PAID_INVOICE,
  PAST_DUE_INVOICE,
  DRAFT_INVOICE,
  PARTIALLY_PAID_INVOICE,
  CANCELLED_INVOICE,
  UNKNOWN_INVOICE,
];

export const BLANK_INVOICE_LINE_ITEM: LineItem = {
  itemName: "",
  itemPrice: 0,
  itemQuantity: 1,
  itemCategory: "",
};

export const CREATE_INVOICE_FORM_TITLE = "Create Invoice";
export const EDIT_INVOICE_FORM_TITLE = "Edit Invoice";

export const ACTIVE_INVOICE_FILTER = "active";
export const PAST_DUE_INVOICE_FILTER = "past_due";
export const PAID_INVOICE_FILTER = "paid";
export const PARTIALLY_PAID_INVOICE_FILTER = "partially_paid";
export const ALL_INVOICE_FILTER = null;

export const VALID_INVOICE_FILTERS = [
  ACTIVE_INVOICE_FILTER,
  PAST_DUE_INVOICE_FILTER,
  PAID_INVOICE_FILTER,
  PARTIALLY_PAID_INVOICE_FILTER,
  ALL_INVOICE_FILTER,
];

export const ACTIVE_INVOICE_FILTER_LABEL = "Status: Active";
export const PAST_DUE_INVOICE_FILTER_LABEL = "Status: Past Due";
export const PAID_INVOICE_FILTER_LABEL = "Status: Paid";
export const PARTIALLY_PAID_INVOICE_FILTER_LABEL = "Status: Partially Paid";
export const ALL_INVOICE_FILTER_LABEL = "Status: All";

export const INVOICE_FILTER_OPTIONS = [
  {
    value: ALL_INVOICE_FILTER,
    label: ALL_INVOICE_FILTER_LABEL,
  },
  { value: ACTIVE_INVOICE_FILTER, label: ACTIVE_INVOICE_FILTER_LABEL },
  { value: PAST_DUE_INVOICE_FILTER, label: PAST_DUE_INVOICE_FILTER_LABEL },
  { value: PAID_INVOICE_FILTER, label: PAID_INVOICE_FILTER_LABEL },
  {
    value: PARTIALLY_PAID_INVOICE_FILTER,
    label: PARTIALLY_PAID_INVOICE_FILTER_LABEL,
  },
];

export const PAYPOINT_STATISTICS_MODE_OPTIONS = [
  {
    label: "Year to date",
    value: AdminV1OrganizationsPaymentsStatisticsListMode.ytd,
  },
  {
    label: "Month to date",
    value: AdminV1OrganizationsPaymentsStatisticsListMode.mtd,
  },
  {
    label: "Week to date",
    value: AdminV1OrganizationsPaymentsStatisticsListMode.wtd,
  },
  {
    label: "All current day",
    value: AdminV1OrganizationsPaymentsStatisticsListMode.today,
  },
  {
    label: "Last 12 months",
    value: AdminV1OrganizationsPaymentsStatisticsListMode.m12,
  },
  {
    label: "Last 30 days",
    value: AdminV1OrganizationsPaymentsStatisticsListMode.d30,
  },
];

export const OFFERING_TYPE_OPTIONS = [
  { label: "All Types", value: null },
  {
    label: "Registration",
    value: AdminV1OrganizationsPaymentsLogListType.registration,
  },
  {
    label: "Membership",
    value: AdminV1OrganizationsPaymentsLogListType.membership,
  },
  { label: "Booking", value: AdminV1OrganizationsPaymentsLogListType.booking },
  { label: "Invoice", value: AdminV1OrganizationsPaymentsLogListType.invoice },
];

export const PAYMENT_STATUS_OPTIONS = [
  { label: "All Statuses", value: null },
  { label: "Paid", value: AdminV1OrganizationsPaymentsLogListStatus.paid },
  { label: "Failed", value: AdminV1OrganizationsPaymentsLogListStatus.failed },
  {
    label: "Refunded",
    value: AdminV1OrganizationsPaymentsLogListStatus.refunded,
  },
  {
    label: "Past Due",
    value: AdminV1OrganizationsPaymentsLogListStatus.past_due,
  },
];

export const DATE_RANGE_OPTIONS = [
  { label: "Week-to-date", value: "week-to-date" },
  { label: "Month-to-date", value: "month-to-date" },
  { label: "Year-to-date", value: "year-to-date" },
  { label: "Last 7 days", value: "last-7-days" },
  { label: "Last 30 days", value: "last-30-days" },
  { label: "Last 3 months", value: "last-3-months" },
  { label: "Last 6 months", value: "last-6-months" },
  { label: "Last 12 months", value: "last-12-months" },
  { label: "All time", value: "all-time" },
];

// Payment method constants
export const PAYMENT_METHOD_PROCESS_NOW = "process-now";
export const PAYMENT_METHOD_CASH = "cash";
export const PAYMENT_METHOD_WAIVE = "waive";

// Payment option constants
export const PAYMENT_OPTION_ONE_TIME = "one-time";
export const PAYMENT_OPTION_INSTALLMENTS = "installments";

// Stored payment method constants
export const STORED_PAYMENT_METHOD_ADD_NEW = "add-new";
export const STORED_PAYMENT_METHOD_ADD_NEW_LABEL = "Add new method";

// Payment options for Payabli
export const PAYMENT_OPTIONS = {
  ONE_TIME: "one-time" as const,
  RECURRING: "recurring" as const,
};

// Payabli card configuration
import type { PayabliCardConfig } from "@/types";

export const PAYABLI_CARD_CONFIG_DISABLED: PayabliCardConfig = {
  enabled: false,
};

export const PAYABLI_CARD_CONFIG_ENABLED: PayabliCardConfig = {
  enabled: true,
  amex: true,
  discover: true,
  visa: true,
  mastercard: true,
  jcb: true,
  diners: true,
  inputs: {
    cardNumber: {
      label: "Card Number",
      placeholder: "1234 1234 1234 1234",
      size: 5,
      row: 0,
      order: 0,
      floating: false,
    },
    cardExpirationDate: {
      label: "MM/YY",
      placeholder: "MM/YY",
      size: 2,
      row: 0,
      order: 1,
      floating: false,
    },
    cardCvv: {
      label: "CVV",
      placeholder: "CVV",
      floating: false,
      size: 2,
      row: 0,
      order: 2,
    },
    cardZipcode: {
      label: "Zip",
      placeholder: "ZIP",
      row: 0,
      order: 3,
      size: 2,
      floating: false,
      country: ["us", "ca"],
    },
    cardHolderName: {
      label: "Cardholder Name",
      placeholder: "Name",
      value: "",
      size: 12,
      row: 4,
      order: 0,
      floating: false,
    },
  },
};

export const getPayabliCardConfig = (enabled: boolean): PayabliCardConfig => {
  return enabled ? PAYABLI_CARD_CONFIG_ENABLED : PAYABLI_CARD_CONFIG_DISABLED;
};

// Payabli component configuration constants
export const PAYABLI_TYPE_METHOD_EMBEDDED = "methodEmbedded";
export const PAYABLI_ROOT_CONTAINER = "pay-component-1";
export const PAYABLI_DEFAULT_OPEN_CARD = "card";
export const PAYABLI_DEFAULT_OPEN_ACH = "ach";
export const PAYABLI_EXEC_METHOD = "method";
export const PAYABLI_EXEC_REINIT = "reinit";
// Payment Status Colors (Tailwind classes)
export const PAYMENT_STATUS_COLOR_SUCCESS = "text-green-500";
export const PAYMENT_STATUS_COLOR_PENDING = "text-blue-500";
export const PAYMENT_STATUS_COLOR_ERROR = "text-red-500";
export const PAYMENT_STATUS_COLOR_NEUTRAL = "text-gray-400";
export const PAYMENT_STATUS_COLOR_DEFAULT = "text-gray-900";
